import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Lock, CheckCircle2, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import SprintrollIcon from "@/components/SprintrollIcon";
import SprintrollLogo from "@/components/SprintrollLogo";
import SEO from "@/components/SEO";
import { useAuth } from "@/components/AuthProvider";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) return toast.error("Token inválido");
    if (pwd.length < 8) return toast.error("La contraseña debe tener al menos 8 caracteres");
    if (pwd !== confirm) return toast.error("Las contraseñas no coinciden");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: pwd });
      setDone(true);
      await refresh();
      toast.success("Contraseña actualizada");
      setTimeout(() => navigate("/", { replace: true }), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo restablecer la contraseña");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen grid place-items-center p-6" data-testid="reset-no-token">
        <div className="max-w-md w-full glass-panel rounded-2xl p-8 text-center space-y-4">
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-rose-100 dark:bg-rose-900/30 text-rose-600">
            <AlertCircle className="h-6 w-6" />
          </div>
          <h1 className="font-display text-2xl font-semibold">Enlace inválido</h1>
          <p className="text-sm text-muted-foreground">
            Este enlace de restablecimiento no es válido o le falta el token. Solicita un nuevo enlace desde el inicio de sesión.
          </p>
          <Link to="/login" className="inline-block">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white">Volver al login</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center p-6 bg-grid-soft" data-testid="reset-password-page">
      <SEO titleKey="seo.reset.title" descriptionKey="seo.reset.description" path="/reset-password" noindex />
      <div className="max-w-md w-full glass-panel rounded-2xl p-8 space-y-5">
        <div className="flex items-center justify-center">
          <SprintrollLogo className="h-9 w-auto" />
        </div>

        {done ? (
          <div className="text-center space-y-3" data-testid="reset-success">
            <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <h1 className="font-display text-2xl font-semibold">¡Listo!</h1>
            <p className="text-sm text-muted-foreground">
              Tu contraseña fue actualizada. Te llevamos a tu dashboard…
            </p>
          </div>
        ) : (
          <>
            <div className="text-center space-y-1">
              <h1 className="font-display text-2xl font-semibold">Nueva contraseña</h1>
              <p className="text-sm text-muted-foreground">Crea una contraseña nueva para tu cuenta.</p>
            </div>
            <form onSubmit={submit} className="space-y-3" data-testid="reset-form">
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="password" autoComplete="new-password" required
                  value={pwd} onChange={(e) => setPwd(e.target.value)}
                  placeholder="Nueva contraseña (mín 8 caracteres)"
                  className="pl-9 bg-white/70 dark:bg-slate-900/40"
                  data-testid="reset-password-input"
                />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="password" autoComplete="new-password" required
                  value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Confirma la contraseña"
                  className="pl-9 bg-white/70 dark:bg-slate-900/40"
                  data-testid="reset-confirm-input"
                />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="reset-submit">
                {loading ? "Actualizando…" : "Restablecer contraseña"}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                <Link to="/login" className="hover:underline">Cancelar</Link>
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
